<template>
  <v-dialog :value="sectionEditor.show" :width="1200" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <h3 class="label-input">Section Name</h3>
          <v-text-field v-model="fields.title" label="Name" />
        </div>
        <div class="mb-4">
          <h3 class="label-input">Review Title</h3>
          <ckeditor :editor="editor" v-model="fields.reviewTitle" :config="editorConfig" />
        </div>
        <div class="mb-6">
          <label class="label-input mb-4 d-flex align-center gap-2" htmlFor="reviewShow" style="width: fit-content">
            <span>Media</span>
            <!-- <input class="ml-2 cursor-pointer" type="checkbox" v-model="fields.image.show" id="imageShow" /> -->
          </label>
          <div class="group-item">
            <v-select v-model="fields.media.position" :items="positionOptions" label="Position" />
            <v-radio-group v-model="fields.media.type" row>
              <v-radio label="Image" value="image"></v-radio>
              <v-radio label="Video" value="video"></v-radio>
            </v-radio-group>
            <div v-if="fields.media.type === 'image'" class="mb-4">
              <UploadImage
                ref="reviewImageUpload"
                @callback="handleUploadReviewImage"
                :image="{ imageSrc: fields.media.url }"
                :height="`150`"
                :typeUpload="'image'"
                @callbackDelete="handleRemoveReviewImage"
              />
            </div>
            <div v-if="fields.media.type === 'video'">
              <div class="d-flex align-center gap-6 mb-4">
                <v-text-field v-model="fields.media.video" label="Video URL" />
                <v-btn @click="handlePreviewVideo" text color="primary"> <v-icon>mdi-reload</v-icon> Reload </v-btn>
              </div>
              <div class="video-wrapper" v-if="fields.media.video">
                <div class="wrap-video">
                  <video width="100%" height="auto" autoplay muted loop playsinline ref="videoPlayer">
                    <source data-attribute="card-video" type="video/mp4" :src="fields.media.video" />
                  </video>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-6">
          <h3 class="label-input">Main Content</h3>
          <div class="group-item">
            <div class="mb-2">Content</div>
            <ckeditor :editor="editor" v-model="fields.content" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <div class="d-flex align-center gap-2">
              <label class="label-input mb-0 d-flex align-center" htmlFor="featureListEnable"
                >Feature List ({{ fields.list.length }})
                <input
                  class="ml-2 cursor-pointer"
                  type="checkbox"
                  v-model="fields.featureListEnable"
                  id="featureListEnable"
              /></label>
            </div>
            <v-btn small color="primary" @click="handleAddListItem">
              <v-icon size="16" left>mdi-plus</v-icon>
              Add Feature
            </v-btn>
          </div>

          <v-row class="mb-4">
            <v-col cols="12" md="3">
              <v-text-field
                type="number"
                v-model.number="fields.itemImageWidthDesktop"
                label="Item Image Width (Desktop)"
                min="0"
                dense
                hide-details
                suffix="px"
                :rules="[v => v >= 0 || 'Width must be >= 0']"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                type="number"
                v-model.number="fields.itemImageWidthMobile"
                label="Item Image Width (Mobile)"
                min="0"
                dense
                hide-details
                suffix="px"
                :rules="[v => v >= 0 || 'Width must be >= 0']"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                type="number"
                v-model.number="fields.itemImageHeightDesktop"
                label="Item Image Height (Desktop)"
                min="0"
                dense
                hide-details
                suffix="px"
                :rules="[v => v >= 0 || 'Height must be >= 0']"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                type="number"
                v-model.number="fields.itemImageHeightMobile"
                label="Item Image Height (Mobile)"
                min="0"
                dense
                hide-details
                suffix="px"
                :rules="[v => v >= 0 || 'Height must be >= 0']"
              />
            </v-col>
          </v-row>

          <div v-if="fields.list.length === 0" class="text-center pa-4 grey--text group-item">
            No features added yet. Click "Add Feature" to get started.
          </div>
          <v-expansion-panels v-else accordion>
            <v-expansion-panel v-for="(item, i) in fields.list" :key="i">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center" style="width: 100%">
                  <div class="d-flex align-center">
                    <div class="feature-thumbnail mr-3">
                      <img v-if="item.image" :src="item.image" alt="" />
                      <v-icon v-else color="grey lighten-1">mdi-image</v-icon>
                    </div>
                    <span v-html="item.text.replace(/<[^>]*>/g, '').trim()"></span>
                  </div>
                  <v-btn icon small @click.stop="handleRemoveListItem(i)">
                    <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="pa-2">
                  <div class="mb-4">
                    <UploadImage
                      :ref="`listImageUpload${i}`"
                      @callback="obj => handleUploadListImage(obj, i)"
                      :image="{ imageSrc: item.image }"
                      :height="`100`"
                      :typeUpload="'image'"
                      @callbackDelete="() => handleRemoveListImage(i)"
                    />
                  </div>

                  <ckeditor :editor="editor" v-model="item.text" :config="editorConfig" />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div class="mb-6">
          <label class="label-input mb-4 d-flex align-center gap-2" htmlFor="buttonShow" style="width: fit-content">
            <span>Button</span>
            <input class="ml-2 cursor-pointer" type="checkbox" v-model="fields.button.show" id="buttonShow" />
          </label>
          <div class="group-item">
            <v-row class="mt-2">
              <v-col cols="12" md="4">
                <v-text-field v-model="fields.button.text" label="Text" dense hide-details />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  type="number"
                  v-model.number="fields.button.minWidth"
                  label="Min Width"
                  min="1"
                  dense
                  hide-details
                  suffix="px"
                  :rules="[v => v > 0 || 'Min Width must be greater than 0']"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  type="number"
                  v-model.number="fields.button.borderRadius"
                  label="Border Radius"
                  min="0"
                  dense
                  hide-details
                  suffix="px"
                  :rules="[v => v >= 0 || 'Border Radius must be greater than or equal 0']"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-6">
          <label class="label-input"> Footer Content </label>
          <ckeditor :editor="editor" v-model="fields.footer" :config="editorConfig" />
        </div>

        <div class="mb-6">
          <div class="d-flex align-center justify-space-between mb-4">
            <label
              class="label-input mb-0 d-flex align-center gap-2"
              htmlFor="customerReviewEnable"
              style="width: fit-content"
            >
              <span>Review</span>
              <input
                class="ml-2 cursor-pointer"
                type="checkbox"
                v-model="fields.customerReview.enable"
                id="customerReviewEnable"
              />
            </label>
            <v-btn small color="primary" @click="handleRandomReview">
              <v-icon size="16" left>mdi-shuffle-variant</v-icon>
              Random
            </v-btn>
          </div>
          <div class="group-item">
            <div class="mb-5">
              <label>Rating</label>
              <v-rating
                color="warning"
                background-color="#c1c1c1"
                empty-icon="mdi-star-outline"
                full-icon="mdi-star"
                half-icon="mdi-star-half-full"
                hover
                length="5"
                size="35"
                v-model="fields.customerReview.rating"
              ></v-rating>
            </div>
            <div class="mb-5">
              <label>Avatar Image</label>
              <UploadImage
                @callback="obj => handleUploadReviewAvatar(obj)"
                :image="{ imageSrc: fields.customerReview.avatar }"
                :height="`150`"
                :typeUpload="'image'"
                @callbackDelete="handleRemoveReviewAvatar"
              />
            </div>
            <div class="mb-5">
              <label>Reviewer Name</label>
              <ckeditor :editor="editor" v-model="fields.customerReview.name" :config="editorConfig" />
            </div>
            <div>
              <label>Content</label>
              <ckeditor :editor="editor" v-model="fields.customerReview.content" :config="editorConfig" />
            </div>
          </div>
        </div>

        <div>
          <label class="label-input">Section Settings</label>
          <div class="group-item">
            <div class="mb-6">
              <div class="font-weight-medium">Spacing</div>
              <v-row>
                <v-col cols="6">
                  <v-checkbox v-model="fields.topPadding" label="Top Spacing" dense hide-details />
                </v-col>
                <v-col cols="6">
                  <v-checkbox v-model="fields.bottomPadding" label="Bottom Spacing" dense hide-details />
                </v-col>
              </v-row>
            </div>
            <!-- <SectionPadding class="mb-6" :fields="fields.sectionPadding" /> -->
            <div class="mb-6">
              <div class="font-weight-medium mb-3">Container Colors</div>
              <v-row>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.backgroundColor"
                    name="backgroundColor"
                    label="Background Color"
                    default-color="#ffffff"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.textColor"
                    name="textColor"
                    label="Text Color"
                    default-color="#000000"
                  />
                </v-col>
              </v-row>
            </div>
            <div>
              <div class="font-weight-medium mb-3">Button Colors</div>
              <v-row>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.buttonBackgroundColor"
                    name="buttonBackgroundColor"
                    label="Button Background Color"
                    default-color="#DD1D1D"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.buttonTextColor"
                    name="buttonTextColor"
                    label="Button Text Color"
                    default-color="#ffffff"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save Changes</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeepObject } from '../common/utils';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import ColorPicker from './common/color-picker.vue';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import productReviewsJson from '@/assets/json/product-reviews.json';
import { replaceImageUrl } from '../common/utils';

export default {
  name: 'cardMediaSectionEditor',
  components: {
    UploadImage,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      positionOptions: [
        {
          text: 'Left',
          value: 'left',
        },
        {
          text: 'Right',
          value: 'right',
        },
      ],
      fields: {
        title: 'Card Media Section',
        reviewTitle: 'Protect Your Family From the Silent Killer',
        enableAnchor: false,
        media: {
          url: '',
          video: '',
          type: 'image',
          position: 'left',
          show: true,
        },
        content: '',
        list: [],
        featureListEnable: true,
        itemImageWidthDesktop: 30,
        itemImageWidthMobile: 30,
        itemImageHeightDesktop: 30,
        itemImageHeightMobile: 30,
        footer: '',
        colors: {
          backgroundColor: '#ffffff',
          textColor: '#000000',
          buttonTextColor: '#ffffff',
          buttonBackgroundColor: '#DD1D1D',
        },
        button: {
          text: 'ORDER NOW AND SAVE!',
          show: true,
          borderRadius: 8,
          minWidth: 400,
        },
        customerReview: {
          avatar: '',
          name: '',
          content: '',
          rating: 5,
          enable: true,
        },
        topPadding: true,
        bottomPadding: true,
        // sectionPadding: {
        //   topDesktop: 40,
        //   bottomDesktop: 40,
        //   topMobile: 16,
        //   bottomMobile: 16,
        // },
      },
    };
  },
  methods: {
    escapeHtml(rawText) {
      return String(rawText || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    normalizeFieldsData(data) {
      const nextData = cloneDeepObject(data || {});
      const rawRating = nextData?.customerReview?.rating;
      const parsedRating = Number(rawRating);
      const rawBorderRadius = nextData?.button?.borderRadius;
      const parsedBorderRadius = Number(
        String(rawBorderRadius ?? '')
          .replace('px', '')
          .trim(),
      );
      const rawMinWidth = nextData?.button?.minWidth;
      const parsedMinWidth = Number(rawMinWidth);

      if (nextData.customerReview) {
        nextData.customerReview.rating = Number.isNaN(parsedRating) ? 0 : parsedRating;
      }
      if (!nextData.button) {
        nextData.button = {
          text: 'ORDER NOW AND SAVE!',
          show: true,
          borderRadius: 8,
          minWidth: 400,
        };
      }
      if (!nextData.media) {
        nextData.media = {
          url: '',
          video: '',
          type: 'image',
          position: 'left',
          show: true,
        };
      }
      if (!nextData.media.type) {
        nextData.media.type = nextData.media.video ? 'video' : 'image';
      }
      if (!nextData.media.video) {
        nextData.media.video = '';
      }
      nextData.button.borderRadius = Number.isNaN(parsedBorderRadius) ? 8 : Math.max(0, parsedBorderRadius);
      nextData.button.minWidth = Number.isNaN(parsedMinWidth) ? 400 : Math.max(1, parsedMinWidth);

      const imageSizeFields = [
        ['itemImageWidthDesktop', 30],
        ['itemImageWidthMobile', 30],
        ['itemImageHeightDesktop', 30],
        ['itemImageHeightMobile', 30],
      ];
      imageSizeFields.forEach(([field, defaultValue]) => {
        const parsed = Number(nextData[field]);
        nextData[field] = Number.isNaN(parsed) ? defaultValue : Math.max(0, parsed);
      });

      return nextData;
    },
    handleClosePopup() {
      this.$emit('close');
    },

    handleSave() {
      const updatedSection = {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtmlFromData.sectionCardMedia(this.sectionEditor.section.html, this.fields),
      };
      this.$emit('update:section', updatedSection);
      this.handleClosePopup();
    },

    // Review image handlers
    handleUploadReviewImage(obj) {
      if (obj && obj.imageSrc) {
        this.fields.media.url = obj.imageSrc;
      }
    },

    handleRemoveReviewImage() {
      this.fields.media.url = '';
    },
    handlePreviewVideo() {
      const video = this.$refs.videoPlayer;
      if (video) {
        video.load();
        video.play();
      }
    },

    // List item handlers
    handleAddListItem() {
      this.fields.list.push({
        image: replaceImageUrl(
          'https://cdn.skyfastimg.com/images/2026/03/11/image-69b12f14b85f39d997e5f968.png?w=50&h=50&q=80',
        ),
        text: `<p><i><strong>Feature Text</strong></i></p>`,
      });
    },

    handleRemoveListItem(index) {
      this.fields.list.splice(index, 1);
    },

    handleUploadListImage(obj, index) {
      if (obj && obj.imageSrc) {
        this.fields.list[index].image = obj.imageSrc;
      }
    },

    handleRemoveListImage(index) {
      this.fields.list[index].image = '';
    },

    handleUploadReviewAvatar(obj) {
      if (obj && obj.imageSrc) {
        this.fields.customerReview.avatar = obj.imageSrc;
      }
    },
    handleRemoveReviewAvatar() {
      this.fields.customerReview.avatar = '';
    },
    handleRandomReview() {
      if (!Array.isArray(productReviewsJson) || productReviewsJson.length === 0) return;

      const validReviews = productReviewsJson.filter(item => item && (item.fullName || item.avatar));
      if (validReviews.length === 0) return;

      const currentKey = `${this.fields.customerReview.name || ''}-${this.fields.customerReview.avatar || ''}`;
      const nonDuplicated = validReviews.filter(item => {
        const itemKey = `${item.fullName || ''}-${item.avatar || ''}`;
        return itemKey !== currentKey;
      });
      const pool = nonDuplicated.length > 0 ? nonDuplicated : validReviews;
      const randomReview = pool[Math.floor(Math.random() * pool.length)];
      const safeName = this.escapeHtml(randomReview.fullName || '');

      this.fields.customerReview.avatar = randomReview.avatar ?? '';
      this.fields.customerReview.name = `<p>${safeName}</p>`;
    },
  },

  mounted() {
    if (this.sectionEditor.sectionExtract) {
      this.fields = this.normalizeFieldsData(this.sectionEditor.sectionExtract);
    }
  },

  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal.sectionExtract) {
          this.fields = this.normalizeFieldsData(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ck-editor__editable_inline {
  min-height: 100px !important;
}
::v-deep .ck-editor__editable {
  min-height: 100px !important;
}

.feature-thumbnail {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

::v-deep .v-text-field--outlined {
  .v-input__control {
    .v-input__slot {
      min-height: 40px;
    }
  }
}

::v-deep .v-textarea {
  &.v-text-field--outlined {
    .v-input__control {
      .v-input__slot {
        min-height: 80px;
      }
    }
  }
}

.video-wrapper {
  background: rgba(0, 0, 0, 0.767);
  .wrap-video {
    max-width: 300px;
    margin: 0 auto;
    video {
      display: block;
      width: 100%;
      height: auto;
    }
  }
}
</style>
